import type { Finding, Rule, RuleOutcome, SectionId, Severity } from '../types';

/**
 * Small helpers shared by every rule module.
 *
 * The goal is that a rule file reads as a list of checks rather than a list of
 * object literals. See `docs/ADDING_A_RULE.md` for the full walkthrough.
 */

/** Identity function that gives us type-checking on rule literals. */
export function defineRule(rule: Rule): Rule {
  return rule;
}

/** The rule passed cleanly. */
export function pass(): RuleOutcome {
  return { score: 1 };
}

/**
 * The rule cannot judge — for example a keyword check with no target role
 * selected. Excluded from scoring entirely rather than scored zero.
 */
export function skip(): RuleOutcome {
  return { score: 0, skipped: true };
}

/** The rule found something. `score` is 0 (total miss) to 1 (nearly fine). */
export function flag(score: number, ...findings: Finding[]): RuleOutcome {
  return { score, findings };
}

/**
 * Builds a `Finding` factory bound to one section, so rule files do not repeat
 * `section: 'headline'` on every finding.
 */
export function findingsFor(section: SectionId) {
  return function build(
    id: string,
    severity: Severity,
    title: string,
    why: string,
    fix: string,
    evidence?: readonly string[]
  ): Finding {
    return {
      id: `${section}.${id}`,
      section,
      severity,
      title,
      why,
      fix,
      ...(evidence && evidence.length > 0 ? { evidence: [...evidence] } : {}),
    };
  };
}
